import { useCallback, useEffect, useState } from 'react';
import { Download, Copy, Link2, Save, RefreshCw, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function QrDynamicCard() {
  const [item, setItem] = useState(null);
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [bust, setBust] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/qr/redirects');
      const main = r.data.items.find((i) => i.slug === 'qr') || r.data.items[0];
      setItem(main); setTarget(main?.target || '');
    } catch { toast.error('No se pudo cargar el QR dinámico'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/qr/redirects/${item.slug}`, { target: target.trim() });
      setItem(r.data); setTarget(r.data.target); setBust((b) => b + 1);
      toast.success('Destino actualizado. El QR impreso ya apunta al nuevo destino.');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(item.short_url); toast.success('Enlace corto copiado'); } catch { toast.error('No se pudo copiar'); } };

  if (!item) return null;
  const img = (fmt, q) => `${API}/admin/qr/redirects/${item.slug}/image.${fmt}?${q}&v=${bust}`;
  return (
    <div className="bg-white rounded-xl border border-[#E7E5E4] p-4 md:col-span-2" data-testid="qr-file-dynamic">
      <div className="flex flex-col md:flex-row gap-4">
        <img src={img('png', 'size=512&logo=1')} alt="QR dinámico" className="w-28 h-28 rounded-lg border border-[#E7E5E4] bg-white shrink-0" data-testid="qr-dynamic-image" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[#1C1917] flex items-center gap-1.5"><QrCode className="w-4 h-4 text-[#264639]" /> QR dinámico propio</h3>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#E3EFE8] text-[#264639] font-medium" data-testid="qr-dynamic-scans">{item.scans} escaneo{item.scans !== 1 ? 's' : ''}</span>
          </div>
          <p className="text-xs text-[#78716C] mt-0.5">Reemplaza al QR de QR.io. Codifica un enlace corto nuestro y podés cambiar a dónde lleva sin reimprimir. Imprimí este de ahora en más.</p>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <code className="px-2 py-1 rounded bg-[#F5F4F0] border border-[#E7E5E4] text-[#264639] truncate" data-testid="qr-dynamic-short-url">{item.short_url}</code>
            <Button size="sm" variant="ghost" onClick={copy} data-testid="qr-dynamic-copy"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <div className="flex-1 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-[#78716C] shrink-0" />
              <input className="flex-1 h-9 rounded-md border border-[#E7E5E4] px-3 text-sm" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="/qr o https://…" data-testid="qr-dynamic-target" />
            </div>
            <Button size="sm" className="bg-[#264639] hover:bg-[#1C352B]" disabled={saving || !target.trim() || target.trim() === item.target} onClick={save} data-testid="qr-dynamic-save"><Save className="w-3.5 h-3.5 mr-1.5" /> Guardar destino</Button>
          </div>
          <p className="text-[11px] text-[#78716C] mt-1">Destino actual: <b data-testid="qr-dynamic-current">{item.target}</b>{item.last_scan_at ? ` · último escaneo ${new Date(item.last_scan_at).toLocaleString('es-PY')}` : ''}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a href={img('png', 'size=2048&download=1')} className="inline-flex" data-testid="qr-download-png-dynamic"><Button size="sm" className="bg-[#264639] hover:bg-[#1C352B]"><Download className="w-3.5 h-3.5 mr-1.5" /> PNG 2000px</Button></a>
            <a href={img('png', 'size=2048&logo=1&download=1')} className="inline-flex" data-testid="qr-download-png-logo-dynamic"><Button size="sm" className="bg-[#264639] hover:bg-[#1C352B]"><Download className="w-3.5 h-3.5 mr-1.5" /> PNG con logo</Button></a>
            <a href={img('svg', 'download=1')} className="inline-flex" data-testid="qr-download-svg-dynamic"><Button size="sm" variant="outline" className="border-[#E7E5E4]"><Download className="w-3.5 h-3.5 mr-1.5" /> SVG vectorial</Button></a>
            <Button size="sm" variant="ghost" onClick={load} data-testid="qr-dynamic-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
